import { CircleMinusIcon, XIcon } from "lucide-react";
import { ReactNode, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@eaigle/utils/cn";

/** Escape-to-close + body-scroll-lock, shared by every portal-rendered dialog here. */
function useDialogA11y(onClose: () => void) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [onClose]);
}

export interface DialogProps {
  id?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}

export interface DialogContentProps {
  id?: string;
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  className?: string;
}

function DialogContent({
  id,
  title,
  description,
  children,
  onClose,
  className,
}: DialogContentProps) {
  useDialogA11y(onClose);

  return (
    <div
      id={id}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      aria-describedby={description ? "dialog-description" : undefined}
      className="fixed inset-0 z-100 flex items-center justify-center p-4 pointer-events-auto"
    >
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden
      />
      <div
        className={cn(
          // Add DialogFooter
          "relative z-101 flex max-h-full w-full max-w-lg flex-col rounded-xl border border-gray-200 bg-white shadow-xl",
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-gray-100 p-5">
          <div>
            <h2 id="dialog-title" className="text-lg font-semibold text-gray-900">
              {title}
            </h2>
            {description && (
              <p id="dialog-description" className="mt-1 text-sm text-gray-500">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-label="Close"
          >
            <XIcon className="size-5" />
          </button>
        </div>
        <div className="min-h-0 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

export function Dialog({
  id,
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
}: DialogProps) {
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  if (!open) return null;

  const content = (
    <DialogContent
      id={id}
      title={title}
      description={description}
      onClose={handleClose}
      className={className}
    >
      {children}
    </DialogContent>
  );

  return createPortal(content, document.body);
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Plain text (header) or custom content (body, e.g. TrailerInfoBlock). */
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "default";
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  onConfirm,
  loading = false,
}: ConfirmDialogProps) {
  const handleConfirm = useCallback(async () => {
    await onConfirm();
    onOpenChange(false);
  }, [onConfirm, onOpenChange]);

  const isDanger = variant === "danger";
  const isStringDescription = typeof description === "string";

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={isStringDescription ? description : undefined}
    >
      {!isStringDescription && description != null && (
        <div className="pb-4" id="dialog-description">
          {description}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button
          id="DialogCancel"
          type="button"
          onClick={() => onOpenChange(false)}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
        >
          {cancelLabel}
        </button>
        <button
          id="DialogConfirm"
          type="button"
          onClick={() => void handleConfirm()}
          disabled={loading}
          className={`rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
            isDanger ?
              "bg-red-600 hover:bg-red-700 focus:ring-red-500"
            : "bg-primary hover:bg-primary/90 focus:ring-primary"
          }`}
        >
          {loading ? "..." : confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

const OK_BUTTON_CLASS =
  "rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2";

export interface AckDialogProps {
  id?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Plain text (shown in header) or custom content (shown in body). */
  description?: ReactNode;
  okLabel?: string;
}

/** Single-purpose acknowledgment: title, optional description, and an OK button. */
export function AckDialog({
  id,
  open,
  onOpenChange,
  title,
  description,
  okLabel = "OK",
}: AckDialogProps) {
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const isStringDescription = typeof description === "string";

  return (
    <Dialog
      id={id}
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={isStringDescription ? description : undefined}
    >
      {!isStringDescription && description != null && (
        <div className="pb-4" id="dialog-description">
          {description}
        </div>
      )}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleClose}
          className={OK_BUTTON_CLASS}
          id="DialogAck"
        >
          {okLabel}
        </button>
      </div>
    </Dialog>
  );
}

export interface ConfirmDiscardDialogProps {
  id?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  /** Secondary, lighter line shown below `message`. */
  helperText?: string;
  cancelLabel?: string;
  confirmLabel: string;
  onConfirm: () => void;
}

/**
 * Compact destructive-confirm modal (Figma: Design system / Modal, node 3440:61562).
 * Purpose-built to match that spec exactly (no close icon, tighter padding/typography
 * than {@link Dialog}) rather than reusing DialogContent's layout.
 */
export function ConfirmDiscardDialog({
  id,
  open,
  onOpenChange,
  title,
  message,
  helperText,
  cancelLabel = "Cancel",
  confirmLabel,
  onConfirm,
}: ConfirmDiscardDialogProps) {
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);
  useDialogA11y(handleClose);

  if (!open) return null;

  const content = (
    <div
      id={id}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-discard-title"
      aria-describedby="confirm-discard-description"
      className="fixed inset-0 z-100 flex items-center justify-center p-4 pointer-events-auto"
    >
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden
      />
      <div
        className="relative z-101 flex w-full max-w-[320px] flex-col items-start gap-4 rounded-xl border border-[#eaeaea] bg-white px-4 pt-4 pb-5 shadow-[0px_2px_6px_rgba(0,0,0,0.04)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-9 items-center justify-center overflow-hidden px-2.5 py-2">
          <h2
            id="confirm-discard-title"
            className="text-base font-semibold leading-6 text-[#262626]"
          >
            {title}
          </h2>
        </div>

        <div className="flex w-full flex-col gap-4">
          <div
            id="confirm-discard-description"
            className="flex w-full flex-col px-2.5 py-2"
          >
            <span className="text-[13px] font-medium leading-[17px] text-[#8a8a8e]">
              {message}
            </span>
            {helperText && (
              <span className="text-[11px] font-normal leading-[17px] text-[#aeaeb2]">
                {helperText}
              </span>
            )}
          </div>

          <div className="flex h-9 w-full items-center justify-end gap-2 px-2.5 py-2">
            <button
              type="button"
              id="ConfirmDiscardCancel"
              onClick={handleClose}
              className="flex items-center justify-center gap-2 rounded-lg border border-[#eaeaea] bg-white px-3 py-2 text-[13px] font-medium text-[#262626] transition-colors hover:bg-gray-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              id="ConfirmDiscardConfirm"
              onClick={() => {
                onConfirm();
                onOpenChange(false);
              }}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-[#f7d0ce] bg-[#fef2f2] px-3 py-2 text-[13px] font-medium text-[#cc2f26] transition-colors hover:bg-[#fee2e2]"
            >
              <CircleMinusIcon className="size-4 shrink-0" />
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
